import React, { Component } from 'react';
import { any, bool, func, shape, string, oneOfType } from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import SearchGlassIcon from './searchGlassIcon';
import SearchIcon from '../Topbar/SearchIcon';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import style from '../../containers/SearchPage/SearchFiltersPrimary/SearchFiltersPrimary.module.css';
import { elb } from '@elbwalker/walker.js';

import { IconSpinner } from '../../components';

import IconHourGlass from './IconHourGlass';
import * as geocoderMapbox from './GeocoderMapbox';
import * as geocoderGoogleMaps from './GeocoderGoogleMaps';
import Geocoder from './GeocoderGoogleMaps';

import css from './LocationAutocompleteInput.module.css';
import { withRouter } from 'react-router';
import { removeLanguagePrefix } from '../../util/urlHelpers';
import { handleSearch, PlaceIdToCountry } from './utils';
import LocationPredictionsList from './LocationPredictionsList';
import PredictionModal from './PredictionModal';
import {
  continentsList,
  continentsListDe,
  mostBookedDestinations,
} from '../../data/countries-list';
import { getStaticCountryData } from '../../data/countriesStaticData';

const DEBOUNCE_WAIT_TIME = 300;
const DEBOUNCE_WAIT_TIME_FOR_SHORT_QUERIES = 1000;
const KEY_CODE_ARROW_UP = 38;
const KEY_CODE_ARROW_DOWN = 40;
const KEY_CODE_ENTER = 13;
const KEY_CODE_TAB = 9;
const KEY_CODE_ESC = 27;
const DIRECTION_UP = 'up';
const DIRECTION_DOWN = 'down';
const TOUCH_TAP_RADIUS = 5;

const getGeocoderVariant = mapProvider => {
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  return isGoogleMapsInUse ? geocoderGoogleMaps : geocoderMapbox;
};

const currentValue = props => {
  const value = props.input.value || {};

  // Handle case where value is a function or not an object
  if (typeof value !== 'object' || value === null) {
    return { search: '', predictions: [], selectedPlace: null };
  }

  const { search = '', predictions = [], selectedPlace = null } = value;

  // Ensure search is a string
  const searchString = typeof search === 'string' ? search : '';

  return { search: searchString, predictions, selectedPlace };
};

class LocationAutocompleteInputImplementation extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    this.state = {
      inputHasFocus: false,
      selectionInProgress: false,
      touchStartedFrom: null,
      highlightedIndex: -1,
      fetchingPlaceDetails: false,
      fetchingPredictions: false,
      isMobile: false,
      inputSearchValue: '',
      selectedPrediction: null,
      isFirefox: false,
      isModalOpen: false,
      isDeleting: false,
      lastValueLength: 0,
      deletionCount: 0,
    };
    this.mouseClickTimeout = null;
    this.input = null;
    this.shortQueryTimeout = null;
    this.deleteTimeout = null;
    this.valueHistory = [];
    this.openModal = false;
    this.getGeocoder = this.getGeocoder.bind(this);
    this.changeHighlight = this.changeHighlight.bind(this);
    this.selectPrediction = this.selectPrediction.bind(this);
    this.selectItemIfNoneSelected = this.selectItemIfNoneSelected.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onChange = this.onChange.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handlePredictionsSelectStart = this.handlePredictionsSelectStart.bind(this);
    this.handlePredictionsSelectMove = this.handlePredictionsSelectMove.bind(this);
    this.handlePredictionsSelectEnd = this.handlePredictionsSelectEnd.bind(this);
    this.finalizeSelection = this.finalizeSelection.bind(this);

    this.predict = debounce(this.predict.bind(this), DEBOUNCE_WAIT_TIME, { leading: true });
  }

  componentDidMount() {
    this.setState({ isFirefox: navigator?.userAgent?.toLowerCase().indexOf('firefox') > -1 });

    const initialValue = this.props.input.value?.search || '';
    this.setState({ inputSearchValue: initialValue });

    if (typeof window !== 'undefined') {
      this._isMounted = true;
      const routeLoc = window.location.pathname;
      if (removeLanguagePrefix(routeLoc) === '/s') {
        this.setState({ routingLocation: true });
      } else {
        this.setState({ routingLocation: false });
      }

      if (removeLanguagePrefix(routeLoc) === '/') {
        this.setState({ routingLocationHome: true });
      } else {
        this.setState({ routingLocationHome: false });
      }

      window.addEventListener('resize', this.resize.bind(this));
      this.resize();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (typeof window !== 'undefined') {
      if (this.shortQueryTimeout) {
        window.clearTimeout(this.shortQueryTimeout);
        this.shortQueryTimeout = null;
      }
      if (this.deleteTimeout) {
        window.clearTimeout(this.deleteTimeout);
        this.deleteTimeout = null;
      }
      if (this.predict && this.predict.cancel) {
        this.predict.cancel();
      }
      this.valueHistory = [];
    }
  }

  // Méthode pour forcer la synchronisation après sélection
  syncValueAfterSelection(selectedValue) {
    // Mettre à jour l'état local immédiatement
    this.setState({
      inputSearchValue: selectedValue,
      highlightedIndex: -1,
      isDeleting: false, // Réinitialiser l'état de suppression
    });

    // Mettre à jour le formulaire
    this.props.input.onChange({
      search: selectedValue,
      predictions: [],
      selectedPlace: null,
    });

    // Forcer le re-render
    this.forceUpdate();
  }
  syncValue() {
    const currentValue = this.state.inputSearchValue || '';
    this.props.input.onChange({
      search: currentValue,
      predictions: currentValue ? handleSearch(currentValue, this.props.lang) : [],
      selectedPlace: null,
    });
  }

  detectDeletion(newValue, oldValue) {
    // Ensure both values are strings
    const newValueStr = typeof newValue === 'string' ? newValue : '';
    const oldValueStr = typeof oldValue === 'string' ? oldValue : '';

    const newLength = newValueStr.length;
    const oldLength = oldValueStr.length;

    if (newLength < oldLength) {
      return true;
    }

    if (newLength === oldLength && newValueStr !== oldValueStr) {
      return oldValueStr.includes(newValueStr) && newValueStr.length < oldValueStr.length;
    }

    return false;
  }

  getGeocoder() {
    if (!this._geocoder) {
      this._geocoder = new Geocoder();
    }
    return this._geocoder;
  }

  resize() {
    if (typeof window !== 'undefined') {
      let currentHideNav = window.innerWidth <= 768;
      if (currentHideNav !== this.state.hideNav) {
        this.setState({ hideNav: currentHideNav });
      }
      this.setState({ isMobile: currentHideNav });
    }
  }
  onKeyDown(e) {
    if (e.key === 'ArrowUp' || e.keyCode === KEY_CODE_ARROW_UP) {
      e.preventDefault();
      this.changeHighlight(DIRECTION_UP);
    } else if (e.key === 'ArrowDown' || e.keyCode === KEY_CODE_ARROW_DOWN) {
      e.preventDefault();
      this.changeHighlight(DIRECTION_DOWN);
    } else if (e.key === 'Enter' || e.keyCode === KEY_CODE_ENTER) {
      const currentSearchValue = this.state.inputSearchValue || '';
      const predictions = currentSearchValue
        ? handleSearch(currentSearchValue, this.props.lang)
        : this.getAllDestinations();

      if (predictions.length > 0) {
        const highlightedPrediction = predictions[this.state.highlightedIndex] || predictions[0];
        if (highlightedPrediction) {
          e.preventDefault();
          e.stopPropagation();
          elb('search search_id:' + highlightedPrediction.name, {
            id: highlightedPrediction.name,
            type: 'destination',
            interaction: 'keyboard',
          });
          this.selectPrediction(highlightedPrediction);
          this.input.blur();
        }
      }
    } else if (e.key === 'Tab' || e.keyCode === KEY_CODE_TAB) {
      const currentSearchValue = this.state.inputSearchValue || '';
      const predictions = currentSearchValue
        ? handleSearch(currentSearchValue, this.props.lang)
        : this.getAllDestinations();

      if (predictions.length > 0) {
        this.selectPrediction(predictions[0]);
      }
      this.input.blur();
    } else if ((e.key === 'Escape' || e.keyCode === KEY_CODE_ESC) && this.input) {
      this.input.blur();
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      this.setState({
        isDeleting: true,
        deletionCount: this.state.deletionCount + 1,
      });

      if (this.shortQueryTimeout) {
        window.clearTimeout(this.shortQueryTimeout);
        this.shortQueryTimeout = null;
      }
      if (this.predict && this.predict.cancel) {
        this.predict.cancel();
      }

      const delay = Math.min(800 + this.state.deletionCount * 150, 2000);
      this.deleteTimeout = window.setTimeout(() => {
        this.setState({
          isDeleting: false,
          deletionCount: 0,
        });
      }, delay);
    }
  }

  onChange(e) {
    const onChange = this.props.input.onChange;
    const predictions = handleSearch(e.target.value, this.props.lang);
    const newValue = e.target.value;
    const oldValue = this.state.inputSearchValue;

    const isDeleting = this.detectDeletion(newValue, oldValue);

    if (this.valueHistory.length > 5) {
      this.valueHistory.shift();
    }
    this.valueHistory.push(oldValue);

    if (this.shortQueryTimeout) {
      window.clearTimeout(this.shortQueryTimeout);
      this.shortQueryTimeout = null;
    }

    if (this.deleteTimeout) {
      window.clearTimeout(this.deleteTimeout);
      this.deleteTimeout = null;
    }

    this.setState({
      inputSearchValue: newValue,
      highlightedIndex: -1,
      isDeleting: isDeleting,
      deletionCount: isDeleting ? this.state.deletionCount + 1 : 0,
      lastValueLength: newValue.length,
    });

    onChange({
      search: newValue,
      predictions: newValue ? predictions : [],
      selectedPlace: null,
    });

    if (!newValue) {
      return;
    }

    if (isDeleting) {
      if (this.shortQueryTimeout) {
        window.clearTimeout(this.shortQueryTimeout);
        this.shortQueryTimeout = null;
      }
      if (this.predict && this.predict.cancel) {
        this.predict.cancel();
      }

      const delay = Math.min(600 + this.state.deletionCount * 100, 1500);
      this.deleteTimeout = window.setTimeout(() => {
        this.setState({
          isDeleting: false,
          deletionCount: 0,
        });
      }, delay);

      return;
    }

    if (typeof window !== 'undefined' && !this.state.isDeleting) {
      if (newValue.length >= 3) {
        this.predict(newValue);
      } else {
        this.shortQueryTimeout = window.setTimeout(() => {
          if (
            this._isMounted &&
            this.state.inputSearchValue === newValue &&
            !this.state.isDeleting
          ) {
            this.predict(newValue);
          }
        }, DEBOUNCE_WAIT_TIME_FOR_SHORT_QUERIES);
      }
    }
  }

  changeHighlight(direction) {
    this.setState((prevState, props) => {
      const currentSearchValue = this.state.inputSearchValue || '';
      let predictions;
      if (currentSearchValue) {
        predictions = handleSearch(currentSearchValue, this.props.lang);
      } else {
        predictions = this.getAllDestinations();
      }

      const currentIndex = prevState.highlightedIndex;
      let index = currentIndex;

      if (direction === DIRECTION_UP) {
        index = currentIndex <= 0 ? 0 : currentIndex - 1;
      } else if (direction === DIRECTION_DOWN) {
        index = currentIndex >= predictions.length - 1 ? predictions.length - 1 : currentIndex + 1;
      }

      if (index === -1 && predictions.length > 0) {
        index = 0;
      }

      return { highlightedIndex: index };
    });
  }

  getAllDestinations() {
    const { lang } = this.props;
    const continentsData = lang === 'de' ? continentsListDe : continentsList;

    return [
      ...mostBookedDestinations.map(dest => ({
        ...dest,
        name: lang === 'de' ? dest.nameDe : dest.nameEn,
      })),
      ...Object.entries(continentsData).flatMap(([key, continent]) =>
        continent.countries.map(country => ({
          ...country,
          name: lang === 'de' ? country.nameDe : country.nameEn,
        }))
      ),
    ];
  }

  selectPrediction(prediction) {
    const currentLocationBoundsDistance = this.props.config.maps?.search
      ?.currentLocationBoundsDistance;
    const lang = this.props?.lang ?? 'de';

    this.props.input.onChange({
      ...this.props.input,
      selectedPlace: null,
    });

    this.setState({
      fetchingPlaceDetails: true,
      selectedPrediction: prediction,
    });

    // Check if prediction already has predictionPlace (from static data)
    // If not, check if we can get static data using nameEn
    let predictionWithStatic = prediction;
    if (!prediction.predictionPlace && prediction.nameEn) {
      const staticData = getStaticCountryData(prediction.nameEn);
      if (staticData) {
        const displayName = lang === 'de' ? prediction.nameDe : prediction.nameEn;
        predictionWithStatic = {
          ...prediction,
          predictionPlace: {
            address: displayName,
            bounds: staticData.bounds,
            origin: staticData.origin,
            country: staticData.countryCode,
          },
        };
      }
    }

    this.getGeocoder()
      .getPlaceDetails(predictionWithStatic, currentLocationBoundsDistance, lang)
      .then(place => {
        if (!this._isMounted) {
          return;
        }
        const country = place.country;

        const origin = `${place.origin.lat},${place.origin.lng}`;

        this.setState({
          fetchingPlaceDetails: false,
          selectedPrediction: null,
          inputSearchValue: place.address, // Mettre à jour l'état local avec l'adresse sélectionnée
          highlightedIndex: -1,
          isDeleting: false, // Réinitialiser l'état de suppression
        });
        this.props.input.onChange({
          search: place.address,
          predictions: [],
          selectedPlace: place,
          origin,
          country,
        });
      })
      .catch(e => {
        this.setState({
          fetchingPlaceDetails: false,
          selectedPrediction: null,
        });
        // eslint-disable-next-line no-console
        console.error(e);
        this.props.input.onChange({
          ...this.props.input.value,
          selectedPlace: null,
        });
      });
  }
  selectItemIfNoneSelected() {
    if (this.state.fetchingPredictions) {
      return;
    }

    const currentSearchValue = this.state.inputSearchValue || '';
    const { selectedPlace } = currentValue(this.props);
    const predictions = handleSearch(currentSearchValue, this.props.lang);

    if (!selectedPlace) {
      if (predictions && predictions.length > 0) {
        const index = this.state.highlightedIndex !== -1 ? this.state.highlightedIndex : 0;
        this.selectPrediction(predictions[index]);
      } else {
        this.predict(currentSearchValue);
      }
    }
  }

  predict(search) {
    const onChange = this.props.input.onChange;

    if (!this._isMounted || this.state.isDeleting) {
      return;
    }

    const currentLength = this.state.inputSearchValue.length;
    if (search.length < currentLength) {
      return;
    }

    if (this.valueHistory.length > 0) {
      const lastValue = this.valueHistory[this.valueHistory.length - 1];
      if (search.length < lastValue.length) {
        return;
      }
    }

    this.setState({ fetchingPredictions: true });

    try {
      const predictions = handleSearch(search, this.props.lang);
      const { search: currentSearch } = currentValue(this.props);

      this.setState({ fetchingPredictions: false });

      if (
        this._isMounted &&
        search === currentSearch &&
        search === this.state.inputSearchValue &&
        !this.state.isDeleting &&
        search.length >= this.state.inputSearchValue.length
      ) {
        onChange({
          search: search,
          predictions: predictions,
          selectedPlace: null,
        });
      }
    } catch (error) {
      this.setState({ fetchingPredictions: false });
      if (this._isMounted) {
        const value = currentValue(this.props);
        onChange({
          ...value,
          selectedPlace: null,
        });
      }
    }
  }

  finalizeSelection() {
    this.setState({ inputHasFocus: false, highlightedIndex: -1 });
    this.props.input.onBlur(currentValue(this.props));
  }

  handleOnBlur() {
    if (this.props.closeOnBlur && !this.state.selectionInProgress) {
      if (!this.state.fetchingPlaceDetails) {
        this.finalizeSelection();
      }
    }
  }

  handlePredictionsSelectStart(touchCoordinates) {
    this.setState({
      selectionInProgress: true,
      touchStartedFrom: touchCoordinates,
      isSwipe: false,
    });
  }

  handlePredictionsSelectMove(touchCoordinates) {
    this.setState(prevState => {
      const touchStartedFrom = prevState.touchStartedFrom;
      const isTouchAction = !!touchStartedFrom;
      const isSwipe = isTouchAction
        ? Math.abs(touchStartedFrom.y - touchCoordinates.y) > TOUCH_TAP_RADIUS
        : false;

      return { selectionInProgress: false, isSwipe };
    });
  }

  handlePredictionsSelectEnd(prediction) {
    let selectAndFinalize = false;

    // Mettre à jour immédiatement l'état local avec le nom de la prédiction
    this.syncValueAfterSelection(prediction.name);

    if (this.state.isFirefox) {
      clearTimeout(this.mouseClickTimeout);
      this.mouseClickTimeout = setTimeout(() => {
        this.forceSelectForFirefox(prediction);
      }, 50);
    } else {
      this.selectPrediction(prediction);
    }
    this.setState(
      prevState => {
        if (!prevState.isSwipe) {
          selectAndFinalize = true;
        }
        return { selectionInProgress: false, touchStartedFrom: null, isSwipe: false };
      },
      () => {
        if (selectAndFinalize) {
          this.selectPrediction(prediction);
          this.finalizeSelection();
        }
      }
    );
  }
  forceSelectForFirefox = prediction => {
    const { input, config } = this.props;
    const currentLocationBoundsDistance = config.maps?.search?.currentLocationBoundsDistance;

    input.onChange({
      search: prediction.description,
      predictions: [],
      selectedPlace: null,
    });

    this.setState({ inputHasFocus: false });
    let language = useIntl()?.locale?.trim() ?? 'de';

    this.getGeocoder()
      .getPlaceDetails(prediction, currentLocationBoundsDistance, language)
      .then(place => {
        this.setState({
          inputSearchValue: place.address,
          highlightedIndex: -1,
          isDeleting: false,
        }); // Mettre à jour l'état local
        input.onChange({
          search: place.address,
          predictions: [],
          selectedPlace: place,
        });
        input.onBlur(input.value);
      })
      .catch(e => {
        input.onBlur(input.value);
      });
  };

  componentDidUpdate(prevProps) {
    if (this.props.focusInput && !prevProps.focusInput && this.input) {
      setTimeout(() => {
        if (this.input) {
          this.input.focus();
          if (this.props.input.onFocus) {
            this.props.input.onFocus();
          }
        }
      }, 0);
    }
  }

  render() {
    const {
      autoFocus,
      rootClassName,
      className,
      iconClassName,
      inputClassName,
      predictionsClassName,
      predictionsAttributionClassName,
      validClassName,
      placeholder,
      input,
      meta,
      inputRef,
      disabled,
      config,
      focusInput,
      isDatePickerOpen,
      section,
      outerWrapperClassName,
      hideSearchButton,
    } = this.props;
    const { name, onFocus } = input;
    const { search } = currentValue(this.props);

    const isTobarSearch = this.props.isTobarSearch;
    const { touched, valid } = meta || {};
    const isValid = valid && touched;
    const currentSearchValue =
      this.state.inputSearchValue !== undefined ? this.state.inputSearchValue : search || '';
    const predictions = handleSearch(currentSearchValue, this.props.lang);

    const geocoderVariant = getGeocoderVariant(config.maps.mapProvider);

    const predictionsLoading =
      this.state.inputSearchValue !== '' &&
      predictions?.length === 1 &&
      predictions[0]?.id === geocoderVariant.CURRENT_LOCATION_ID;

    const handleOnFocus = e => {
      this.setState({ inputHasFocus: true });
      onFocus(e);
    };

    const rootClass = classNames(
      rootClassName || css.root,
      className,
      section === 'hero' && css.rootHeroMobile
    );
    const iconClass = classNames(iconClassName || css.icon);
    const inputClass = classNames(inputClassName, { [validClassName]: isValid }, section === 'hero' && css.inputHeroMobile);
    const predictionsClass = classNames(predictionsClassName);
    const isCurrentLocationHome = this.state.routingLocationHome;
    const renderPredictions =
      this.state.inputHasFocus || (focusInput && !isDatePickerOpen && this.state.routingLocation);

    const shouldShowPredictions = renderPredictions && !this.state.isDeleting;

    const GeocoderAttribution = geocoderVariant.GeocoderAttribution;
    const isMobileDevice = this.state.isMobile;

    const handleClick = () => {
      const { lang } = this.props;
      this.syncValue();

      const currentValue = this.state.inputSearchValue || '';
      if (currentValue === '') {
        this.props.history.push(`/${lang}/s`);
      } else {
        this.selectItemIfNoneSelected();
      }
    };

    const elbwalkerstatement =
      typeof window !== 'undefined'
        ? removeLanguagePrefix(window?.location?.pathname) == '/s'
          ? 'click:click destp-sinput'
          : 'click:click lp-hero-sinput'
        : 'click:click sinput';

    const setInputRef = node => {
      this.input = node;
      if (inputRef) {
        inputRef(node);
      }
      if (node && focusInput) {
        setTimeout(() => {
          if (this.input) {
            this.input.focus();
            if (this.props.input.onFocus) {
              this.props.input.onFocus();
            }
          }
        }, 0);
      }
    };

    return (
      <div className={outerWrapperClassName}>
        <PredictionModal
          renderPredictions={shouldShowPredictions}
          lang={this.props.lang}
          onChange={this.onChange}
          rootClassName={predictionsClass}
          predictions={predictions}
          predictionsLoading={predictionsLoading}
          currentLocationId={geocoderVariant.CURRENT_LOCATION_ID}
          isGoogleMapsInUse={config.maps.mapProvider === 'googleMaps'}
          geocoder={this.getGeocoder()}
          highlightedIndex={this.state.highlightedIndex}
          onSelectStart={this.handlePredictionsSelectStart}
          onSelectMove={this.handlePredictionsSelectMove}
          onSelectEnd={this.handlePredictionsSelectEnd}
          search={currentSearchValue}
          predictionsAttributionClassName={predictionsAttributionClassName}
          section={section}
        />

        <div className={css.container}>
          <div className={rootClass} data-elbaction={elbwalkerstatement}>
            <div className={iconClass}>
              {this.state.fetchingPlaceDetails ? (
                <IconSpinner className={css.iconSpinner} />
              ) : (
                <IconHourGlass />
              )}
            </div>

            <input
              className={`${inputClass} ${style.searchInputMobileField}`}
              type="search"
              autoComplete="off"
              autoFocus={autoFocus}
              placeholder={
                placeholder === true
                  ? this.props.lang === 'de'
                    ? "Wo geht's hin?"
                    : 'Where to next?'
                  : placeholder
              }
              name={name}
              value={currentSearchValue}
              disabled={disabled || this.state.fetchingPlaceDetails}
              onFocus={handleOnFocus}
              onBlur={this.handleOnBlur}
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              ref={setInputRef}
            />
            {isTobarSearch ? (
              <SearchIcon
                className={classNames(
                  css.searchInputEndIcon,
                  section === 'navbar' && css.searchInputEndIconNavbar,
                  section === 'search' && css.searchInputEndIconSearch
                )}
              />
            ) : null}
            <div className={css.predictionsContainer}>
              {shouldShowPredictions ? (
                <LocationPredictionsList
                  rootClassName={predictionsClass}
                  predictions={predictions}
                  predictionsLoading={predictionsLoading}
                  currentLocationId={geocoderVariant.CURRENT_LOCATION_ID}
                  isGoogleMapsInUse={config.maps.mapProvider === 'googleMaps'}
                  geocoder={this.getGeocoder()}
                  highlightedIndex={this.state.highlightedIndex}
                  onSelectStart={this.handlePredictionsSelectStart}
                  onSelectMove={this.handlePredictionsSelectMove}
                  onSelectEnd={this.handlePredictionsSelectEnd}
                  lang={this.props.lang}
                  search={currentSearchValue}
                >
                  <GeocoderAttribution className={predictionsAttributionClassName} />
                </LocationPredictionsList>
              ) : null}
            </div>
            {/* Legacy home-page CTA. Callers that ship their own submit control
                (e.g. DestinationSearchBar) opt out with hideSearchButton. */}
            {isCurrentLocationHome && section !== 'navbar' && !hideSearchButton ? (
              <button
                type="button"
                className={classNames(
                  isTobarSearch ? css.searchButton : css.locationSearchBtn,
                  isTobarSearch && section === 'listing' && css.searchButtonListing
                )}
                onClick={handleClick}
                data-elbaction={
                  section === 'listing'
                    ? 'click:click lp-listing-searchcta'
                    : 'click:click lp-hero-searchcta'
                }
                disabled={predictionsLoading}
              >
                {isMobileDevice ? (
                  <SearchGlassIcon />
                ) : isTobarSearch ? (
                  <FormattedMessage id="navbar.searchButtonText" />
                ) : (
                  <FormattedMessage id="landing.bookingButton" />
                )}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

const LocationAutocompleteInputImpl = props => {
  const config = useConfiguration();
  const langFromIntl = useIntl().locale.trim();
  return <LocationAutocompleteInputImplementation config={config} lang={langFromIntl} {...props} />;
};

LocationAutocompleteInputImpl.defaultProps = {
  autoFocus: false,
  closeOnBlur: true,
  rootClassName: null,
  className: null,
  iconClassName: null,
  inputClassName: null,
  predictionsClassName: null,
  predictionsAttributionClassName: null,
  validClassName: null,
  placeholder: '',
  useDefaultPredictions: true,
  meta: null,
  inputRef: null,
  focusInput: false,
  isDatePickerOpen: false,
  hideSearchButton: false,
};

LocationAutocompleteInputImpl.propTypes = {
  autoFocus: bool,
  rootClassName: string,
  className: string,
  closeOnBlur: bool,
  iconClassName: string,
  inputClassName: string,
  predictionsClassName: string,
  predictionsAttributionClassName: string,
  validClassName: string,
  placeholder: string,
  useDefaultPredictions: bool,
  hideSearchButton: bool,
  input: shape({
    name: string.isRequired,
    value: oneOfType([
      shape({
        search: string,
        predictions: any,
        selectedPlace: propTypes.place,
      }),
      string,
    ]),
    onChange: func.isRequired,
    onFocus: func.isRequired,
    onBlur: func.isRequired,
  }).isRequired,
  meta: shape({
    valid: bool.isRequired,
    touched: bool.isRequired,
  }),
  inputRef: func,
  focusInput: bool,
  isDatePickerOpen: bool,
};

export default withRouter(LocationAutocompleteInputImpl);
